import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { createLogger } from '../../../../utils/logger';
const logger = createLogger('FeedingTubePreferencesInput');

import {
  TemporaryFeedingTubeDetails,
  LongTermFeedingTubeDetails,
  TrialPeriodFeedingTubeDetails,
  DeclineFeedingTubeDetails,
  FamilyDecideFeedingTubeDetails,
  FeedingTubeDetails,
  feedingTubePreferencesService
} from '../../../../services/feedingTubePreferencesInfo';

type FeedingTubePreferencesInputScreenRouteProp = RouteProp<RootStackParamList, 'FeedingTubePreferencesInput'>;

type FeedingTubeType = 'temporary' | 'long_term' | 'trial' | 'decline' | 'family_decide' | null;
type LocationType = 'living_will' | 'living_trust' | 'advance_directive' | 'none' | null;

export const FeedingTubePreferencesInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<FeedingTubePreferencesInputScreenRouteProp>();

  const { feedingTubePreferencesData, isEditing } = route.params || {};

  const [selectedFeedingTube, setSelectedFeedingTube] = useState<FeedingTubeType>(
    feedingTubePreferencesData?.feedingTubeType as FeedingTubeType || null
  );
  const [selectedLocation, setSelectedLocation] = useState<LocationType>(
    feedingTubePreferencesData?.locationOfDirective as LocationType || null
  );
  const [specialInstructions, setSpecialInstructions] = useState(
    feedingTubePreferencesData?.specialInstructions || ''
  );
  const [saving, setSaving] = useState(false);

  // Initialize details with existing data if editing
  const getInitialDetails = () => {
    if (feedingTubePreferencesData?.feedingTubeDetails && feedingTubePreferencesData.feedingTubeType) {
      return feedingTubePreferencesData.feedingTubeDetails;
    }
    return null;
  };

  const initialDetails = getInitialDetails();

  // Temporary Feeding Tube Details
  const [temporaryDetails, setTemporaryDetails] = useState<TemporaryFeedingTubeDetails>({
    acceptableCircumstances: (initialDetails as TemporaryFeedingTubeDetails)?.acceptableCircumstances || '',
    maxDuration: (initialDetails as TemporaryFeedingTubeDetails)?.maxDuration || '',
    specificConditions: (initialDetails as TemporaryFeedingTubeDetails)?.specificConditions || '',
    discussedWithHealthcareProvider: (initialDetails as TemporaryFeedingTubeDetails)?.discussedWithHealthcareProvider || false,
  });

  // Long Term Feeding Tube Details
  const [longTermDetails, setLongTermDetails] = useState<LongTermFeedingTubeDetails>({
    warrantsConditions: (initialDetails as LongTermFeedingTubeDetails)?.warrantsConditions || '',
    tubeTypePreferences: (initialDetails as LongTermFeedingTubeDetails)?.tubeTypePreferences || '',
    qualityOfLifeFactors: (initialDetails as LongTermFeedingTubeDetails)?.qualityOfLifeFactors || '',
    ongoingCarePreferences: (initialDetails as LongTermFeedingTubeDetails)?.ongoingCarePreferences || '',
  });

  // Trial Period Details
  const [trialDetails, setTrialDetails] = useState<TrialPeriodFeedingTubeDetails>({
    trialDuration: (initialDetails as TrialPeriodFeedingTubeDetails)?.trialDuration || '',
    continueCriteria: (initialDetails as TrialPeriodFeedingTubeDetails)?.continueCriteria || '',
    evaluationFactors: (initialDetails as TrialPeriodFeedingTubeDetails)?.evaluationFactors || '',
    decisionMakingProcess: (initialDetails as TrialPeriodFeedingTubeDetails)?.decisionMakingProcess || '',
  });

  // Decline Details
  const [declineDetails, setDeclineDetails] = useState<DeclineFeedingTubeDetails>({
    reasonsForDeclining: (initialDetails as DeclineFeedingTubeDetails)?.reasonsForDeclining || '',
    exceptions: (initialDetails as DeclineFeedingTubeDetails)?.exceptions || '',
    alternativeNutritionMethods: (initialDetails as DeclineFeedingTubeDetails)?.alternativeNutritionMethods || '',
    discussedAlternatives: (initialDetails as DeclineFeedingTubeDetails)?.discussedAlternatives || false,
  });

  // Family Decide Details
  const [familyDecideDetails, setFamilyDecideDetails] = useState<FamilyDecideFeedingTubeDetails>({
    specificDecisionMaker: (initialDetails as FamilyDecideFeedingTubeDetails)?.specificDecisionMaker || '',
    factorsToConsider: (initialDetails as FamilyDecideFeedingTubeDetails)?.factorsToConsider || '',
    circumstancesToKnow: (initialDetails as FamilyDecideFeedingTubeDetails)?.circumstancesToKnow || '',
    valuesDiscussed: (initialDetails as FamilyDecideFeedingTubeDetails)?.valuesDiscussed || false,
  });

  const handleSave = async () => {
    // Validation - require at least feeding tube type selection
    if (!selectedFeedingTube) {
      Alert.alert('Error', 'Please select a feeding tube preference type');
      return;
    }

    try {
      setSaving(true);

      // Determine which feeding tube details to save based on selected type
      let feedingTubeDetails: FeedingTubeDetails = null;

      switch (selectedFeedingTube) {
        case 'temporary':
          feedingTubeDetails = temporaryDetails;
          break;
        case 'long_term':
          feedingTubeDetails = longTermDetails;
          break;
        case 'trial':
          feedingTubeDetails = trialDetails;
          break;
        case 'decline':
          feedingTubeDetails = declineDetails;
          break;
        case 'family_decide':
          feedingTubeDetails = familyDecideDetails;
          break;
      }

      const feedingTubeUpdateData = {
        feedingTubeType: selectedFeedingTube || '',
        locationOfDirective: selectedLocation || '',
        specialInstructions: specialInstructions,
        feedingTubeDetails,
        notes: specialInstructions, // Map specialInstructions to notes field
      };

      let savedFeedingTubePreferences;

      if (isEditing && feedingTubePreferencesData?.id) {
        // Update existing record
        savedFeedingTubePreferences = await feedingTubePreferencesService.updateFeedingTubePreferences(
          feedingTubePreferencesData.id,
          feedingTubeUpdateData
        );
        // Handle wrapped response format { success, data, message }
        const updatedData = savedFeedingTubePreferences?.data || savedFeedingTubePreferences;
        logger.debug('Feeding tube preferences updated successfully with ID:', updatedData.id);

        // Navigate to lock/unlock screen to allow re-selection
        navigation.navigate('FeedingTubePreferencesLockUnlock', { feedingTubePreferencesId: updatedData.id });
      } else {
        // Create new record
        savedFeedingTubePreferences = await feedingTubePreferencesService.createFeedingTubePreferences(feedingTubeUpdateData);
        // Handle wrapped response format { success, data, message }
        const createdData = savedFeedingTubePreferences?.data || savedFeedingTubePreferences;
        logger.debug('Feeding tube preferences saved successfully with ID:', createdData.id);

        // Navigate to lock/unlock screen with the ID
        navigation.navigate('FeedingTubePreferencesLockUnlock', { feedingTubePreferencesId: createdData.id });
      }
    } catch (error) {
      logger.error('Error saving feeding tube preferences:', error);
      Alert.alert('Error', 'Failed to save feeding tube preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const renderFeedingTubeDetailsInput = () => {
    switch (selectedFeedingTube) {
      case 'temporary':
        return (
          <View>
            <Text style={styles.detailsTitle}>Temporary Feeding Tube Details:</Text>

            {/* Acceptable circumstances */}
            <Text style={styles.inputLabel}>Under what circumstances would you accept temporary feeding tube use?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe acceptable circumstances..."
              value={temporaryDetails.acceptableCircumstances}
              onChangeText={(value) =>
                setTemporaryDetails({ ...temporaryDetails, acceptableCircumstances: value })
              }
              multiline
            />

            {/* Maximum duration */}
            <Text style={styles.inputLabel}>What is your preferred maximum duration for temporary use?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Specify maximum duration..."
              value={temporaryDetails.maxDuration}
              onChangeText={(value) =>
                setTemporaryDetails({ ...temporaryDetails, maxDuration: value })
              }
              multiline
            />

            {/* Specific conditions */}
            <Text style={styles.inputLabel}>Are there specific conditions that would change your preference?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe specific conditions..."
              value={temporaryDetails.specificConditions}
              onChangeText={(value) =>
                setTemporaryDetails({ ...temporaryDetails, specificConditions: value })
              }
              multiline
            />
          </View>
        );
      case 'long_term':
        return (
          <View>
            <Text style={styles.detailsTitle}>Long-Term Feeding Tube Details:</Text>

            {/* Warranting conditions */}
            <Text style={styles.inputLabel}>What conditions would warrant long-term feeding tube use?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe warranting conditions..."
              value={longTermDetails.warrantsConditions}
              onChangeText={(value) =>
                setLongTermDetails({ ...longTermDetails, warrantsConditions: value })
              }
              multiline
            />

            {/* Tube type preferences */}
            <Text style={styles.inputLabel}>Do you have preferences for the type of feeding tube?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Specify tube type preferences..."
              value={longTermDetails.tubeTypePreferences}
              onChangeText={(value) =>
                setLongTermDetails({ ...longTermDetails, tubeTypePreferences: value })
              }
              multiline
            />

            {/* Quality of life factors */}
            <Text style={styles.inputLabel}>What quality of life factors are important to you?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe quality of life factors..."
              value={longTermDetails.qualityOfLifeFactors}
              onChangeText={(value) =>
                setLongTermDetails({ ...longTermDetails, qualityOfLifeFactors: value })
              }
              multiline
            />

            {/* Ongoing care preferences */}
            <Text style={styles.inputLabel}>What are your preferences for ongoing care and monitoring?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe ongoing care preferences..."
              value={longTermDetails.ongoingCarePreferences}
              onChangeText={(value) =>
                setLongTermDetails({ ...longTermDetails, ongoingCarePreferences: value })
              }
              multiline
            />
          </View>
        );
      case 'trial':
        return (
          <View>
            <Text style={styles.detailsTitle}>Trial Period Details:</Text>

            {/* Trial duration */}
            <Text style={styles.inputLabel}>How long would you want the trial period to last?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Specify trial duration..."
              value={trialDetails.trialDuration}
              onChangeText={(value) =>
                setTrialDetails({ ...trialDetails, trialDuration: value })
              }
              multiline
            />

            {/* Continue criteria */}
            <Text style={styles.inputLabel}>What outcomes would make you want to continue or discontinue?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe continue/discontinue criteria..."
              value={trialDetails.continueCriteria}
              onChangeText={(value) =>
                setTrialDetails({ ...trialDetails, continueCriteria: value })
              }
              multiline
            />

            {/* Evaluation factors */}
            <Text style={styles.inputLabel}>What factors should be evaluated during the trial?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe evaluation factors..."
              value={trialDetails.evaluationFactors}
              onChangeText={(value) =>
                setTrialDetails({ ...trialDetails, evaluationFactors: value })
              }
              multiline
            />

            {/* Decision making process */}
            <Text style={styles.inputLabel}>Who should be involved in the decision-making process?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe decision-making process..."
              value={trialDetails.decisionMakingProcess}
              onChangeText={(value) =>
                setTrialDetails({ ...trialDetails, decisionMakingProcess: value })
              }
              multiline
            />
          </View>
        );
      case 'decline':
        return (
          <View>
            <Text style={styles.detailsTitle}>Decline Feeding Tube Details:</Text>

            {/* Reasons for declining */}
            <Text style={styles.inputLabel}>What are your reasons for declining feeding tube use?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe reasons for declining..."
              value={declineDetails.reasonsForDeclining}
              onChangeText={(value) =>
                setDeclineDetails({ ...declineDetails, reasonsForDeclining: value })
              }
              multiline
            />

            {/* Exceptions */}
            <Text style={styles.inputLabel}>Are there any exceptions to this decision?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe any exceptions..."
              value={declineDetails.exceptions}
              onChangeText={(value) =>
                setDeclineDetails({ ...declineDetails, exceptions: value })
              }
              multiline
            />

            {/* Alternative nutrition methods */}
            <Text style={styles.inputLabel}>What alternative nutrition methods would you prefer?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe alternative nutrition methods..."
              value={declineDetails.alternativeNutritionMethods}
              onChangeText={(value) =>
                setDeclineDetails({ ...declineDetails, alternativeNutritionMethods: value })
              }
              multiline
            />
          </View>
        );
      case 'family_decide':
        return (
          <View>
            <Text style={styles.detailsTitle}>Family Decision Details:</Text>

            {/* Specific decision maker */}
            <Text style={styles.inputLabel}>Who specifically should make this decision?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Name specific decision maker..."
              value={familyDecideDetails.specificDecisionMaker}
              onChangeText={(value) =>
                setFamilyDecideDetails({ ...familyDecideDetails, specificDecisionMaker: value })
              }
              multiline
            />

            {/* Factors to consider */}
            <Text style={styles.inputLabel}>What factors should they consider?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe factors to consider..."
              value={familyDecideDetails.factorsToConsider}
              onChangeText={(value) =>
                setFamilyDecideDetails({ ...familyDecideDetails, factorsToConsider: value })
              }
              multiline
            />

            {/* Circumstances to know */}
            <Text style={styles.inputLabel}>Are there any circumstances you want them to know about?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe circumstances..."
              value={familyDecideDetails.circumstancesToKnow}
              onChangeText={(value) =>
                setFamilyDecideDetails({ ...familyDecideDetails, circumstancesToKnow: value })
              }
              multiline
            />
          </View>
        );
      default:
        return null;
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>
          What are your Feeding Tube Preferences?
        </Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView}>
          <View style={styles.radioGroup}>
            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedFeedingTube('temporary')}
            >
              <View style={styles.radioButton}>
                {selectedFeedingTube === 'temporary' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>Temporary Use</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedFeedingTube('long_term')}
            >
              <View style={styles.radioButton}>
                {selectedFeedingTube === 'long_term' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>Long-Term Use</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedFeedingTube('trial')}
            >
              <View style={styles.radioButton}>
                {selectedFeedingTube === 'trial' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>Trial Period</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedFeedingTube('decline')}
            >
              <View style={styles.radioButton}>
                {selectedFeedingTube === 'decline' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>Decline Feeding Tube</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedFeedingTube('family_decide')}
            >
              <View style={styles.radioButton}>
                {selectedFeedingTube === 'family_decide' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>Let Family Member Decide</Text>
            </TouchableOpacity>
          </View>

          {renderFeedingTubeDetailsInput()}

          <Text style={styles.label}>Feeding tube instructions:</Text>
          <TextInput
            style={styles.specialInstructionsInput}
            placeholder="Enter any special instructions or additional preferences regarding feeding tube use..."
            value={specialInstructions}
            onChangeText={setSpecialInstructions}
            multiline
          />

          <Text style={styles.sectionTitle}>
            Where is your feeding tube preference located?
          </Text>

          <View style={styles.radioGroup}>
            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedLocation('living_will')}
            >
              <View style={styles.radioButton}>
                {selectedLocation === 'living_will' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>In your living Will</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedLocation('living_trust')}
            >
              <View style={styles.radioButton}>
                {selectedLocation === 'living_trust' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>In your living Trust</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedLocation('advance_directive')}
            >
              <View style={styles.radioButton}>
                {selectedLocation === 'advance_directive' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>In your advanced directive</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedLocation('none')}
            >
              <View style={styles.radioButton}>
                {selectedLocation === 'none' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>None of the above</Text>
            </TouchableOpacity>
          </View>

          <TouchableOpacity
            style={[styles.saveButton, saving && styles.saveButtonDisabled]}
            onPress={handleSave}
            disabled={saving}
          >
            {saving ? (
              <ActivityIndicator color="#FFFFFF" />
            ) : (
              <Text style={styles.saveButtonText}>Save</Text>
            )}
          </TouchableOpacity>

          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(125),
    height: s(125),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
    paddingHorizontal: ms(30),
    textAlign: 'center',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
  },
  radioGroup: {
    marginBottom: ms(20),
  },
  radioOption: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(20),
  },
  radioButton: {
    width: s(24),
    height: s(24),
    borderRadius: ms(12),
    borderWidth: ms(2),
    borderColor: '#1C2C4F',
    marginRight: ms(10),
    justifyContent: 'center',
    alignItems: 'center',
  },
  radioButtonSelected: {
    width: s(12),
    height: s(12),
    borderRadius: ms(6),
    backgroundColor: '#1C2C4F',
  },
  radioText: {
    fontSize: ms(18, 0.3),
    color: '#1C2C4F',
  },
  detailsTitle: {
    fontSize: ms(16, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  detailsInput: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(10),
    padding: ms(15),
    marginBottom: ms(20),
    minHeight: s(100),
    textAlignVertical: 'top',
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
  },
  inputLabel: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(8),
    marginTop: ms(12),
  },
  structuredInput: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(10),
    padding: ms(15),
    marginBottom: ms(20),
    minHeight: s(80),
    textAlignVertical: 'top',
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  sectionTitle: {
    fontSize: ms(20, 0.3),
    fontWeight: 'bold',
    color: '#F2994A',
    marginBottom: ms(20),
  },
  label: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(10),
  },
  specialInstructionsInput: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(10),
    padding: ms(15),
    marginBottom: ms(30),
    minHeight: s(150),
    textAlignVertical: 'top',
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
  },
  saveButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
    marginBottom: ms(30),
  },
  saveButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  saveButtonDisabled: {
    backgroundColor: '#A0A0A0',
  },
  bottomPadding: {
    height: s(120),
  },
});